import { useCallback, useContext, useEffect, useState } from 'preact/hooks';

import { GlobalsContext } from '@/shared/context';
import { trackMP } from '@/shared/mp';

import { AnimalAlertType } from './constants';
import { AnimalAlertAck, UseAnimalAlertOptions } from './types';
import {
  clearFlaggedAnimalAlertType,
  getFlaggedAnimalAlertType,
  setFlaggedAnimalAlertType,
  useFlaggedAnimalAlertType,
} from './useFlaggedAnimalAlertType';

type EmitAnimalAlertResult = 'accepted' | 'denied' | 'cooldown' | 'unavailable';

interface AnimalAlertRemoteState {
  userCount: number;
  remainingMs: number;
}

export const useAnimalAlert = ({ habitatId, cameraId, entryPoint }: UseAnimalAlertOptions) => {
  const { socket } = useContext(GlobalsContext);
  const flaggedAlertType = useFlaggedAnimalAlertType(habitatId);

  const emitAnimalAlert = useCallback(
    (alertType: AnimalAlertType): Promise<EmitAnimalAlertResult> => {
      if (!socket || !habitatId || !cameraId) {
        return Promise.resolve('unavailable');
      }

      if (getFlaggedAnimalAlertType(habitatId)) {
        return Promise.resolve('cooldown');
      }

      setFlaggedAnimalAlertType(habitatId, alertType);

      return new Promise((resolve) => {
        socket.emit(
          'flagAnimalAlert',
          {
            habitatId,
            cameraId,
            alertType,
          },
          (ack: AnimalAlertAck) => {
            if (ack.ok && ack.status === 'accepted') {
              resolve('accepted');
              return;
            }

            if (!ack.ok && ack.status === 'cooldown') {
              resolve('cooldown');
              return;
            }

            clearFlaggedAnimalAlertType(habitatId);
            resolve('denied');
          },
        );
        // Track the animal alert event no matter it is success or failure
        trackMP('animal-alert', {
          alertType,
          habitatId,
          cameraId,
          entryPoint,
        });
      });
    },
    [cameraId, habitatId, socket, entryPoint],
  );

  return { emitAnimalAlert, flaggedAlertType };
};

export const useCurrentAnimalAlertRemoteState = ({ habitatId, cameraId }: Omit<UseAnimalAlertOptions, 'entryPoint'>) => {
  const { socket } = useContext(GlobalsContext);
  const [animalAlertRemoteState, setAnimalAlertRemoteState] = useState<AnimalAlertRemoteState | null>(null);

  useEffect(() => {
    if (!socket || !habitatId || !cameraId) {
      return undefined;
    }

    setAnimalAlertRemoteState({ userCount: 0, remainingMs: 0 });

    const ackHandler = (ack: AnimalAlertRemoteState) => {
      setAnimalAlertRemoteState(ack);
    };
    const eventHandler = (event: { habitatId: string; cameraId: string; userCount: number; remainingMs: number }) => {
      if (event.habitatId !== habitatId || event.cameraId !== cameraId) {
        return;
      }
      setAnimalAlertRemoteState({ userCount: event.userCount, remainingMs: event.remainingMs });
    };

    socket.emit('getAnimalAlert', { habitatId, cameraId }, ackHandler);
    socket.on('flaggedAnimalAlert', eventHandler);

    return () => {
      socket.off('flaggedAnimalAlert', eventHandler);
    };
  }, [habitatId, cameraId, socket]);

  return animalAlertRemoteState;
};
